import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';

export interface AuthSession {
  /** Whether the session is still being loaded */
  isLoading: boolean;
  /** Whether the user has a valid authenticated session */
  isAuthenticated: boolean;
  /** The authenticated user's email address from the ID token */
  email: string | null;
  /** The raw ID token string for use as a Bearer token */
  idToken: string | null;
  /** Sign out the current user and redirect */
  handleSignOut: () => Promise<void>;
  /** Refresh the session (re-fetch tokens) */
  refreshSession: () => Promise<void>;
  /** Error message if session fetch failed */
  error: string | null;
}

/**
 * Hook wrapping Amplify `fetchAuthSession()` to get the current user session,
 * email, and ID token. Handles token expiry detection and sign-out.
 *
 * Requirements: 1.1, 1.5, 1.7, 1.8
 */
export function useAuth(): AuthSession {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadSession = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const session = await fetchAuthSession();
      const token = session.tokens?.idToken;

      if (!token) {
        setIsAuthenticated(false);
        setEmail(null);
        setIdToken(null);
        return;
      }

      const tokenString = token.toString();
      const payload = token.payload;

      setIsAuthenticated(true);
      setIdToken(tokenString);
      setEmail((payload.email as string) ?? null);
    } catch (err) {
      setIsAuthenticated(false);
      setEmail(null);
      setIdToken(null);
      setError(
        err instanceof Error ? err.message : 'Failed to load session'
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
    } finally {
      setIsAuthenticated(false);
      setEmail(null);
      setIdToken(null);
    }
  }, []);

  return {
    isLoading,
    isAuthenticated,
    email,
    idToken,
    handleSignOut,
    refreshSession: loadSession,
    error,
  };
}
